import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Link,
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  ToastTrigger,
  tokens
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { FormattedMessage } from "react-intl";
import { RoomPersistenceApi } from "../../api/RoomPersistenceApi";
import { buildSummaryRequestPayload, resolveRoomDisplayName, resolveRoomUserId, sanitizeSummaryDataSources } from "../../utils/room-persistence-utils";
import { handleStorageLimitError } from "../../utils/storage-limit-utils";

const OPENAI_SUMMARY_PROVIDER = "OpenAI Summary";
const DOWNLOAD_SUMMARY_LABEL = "Download summary";
const SUMMARY_STARTED_MIN_VISIBLE_MS = 1500;
const summaryToastBodyStyle = {
  lineHeight: tokens.lineHeightBase300
};

function renderSummaryToastDismissAction() {
  return (
    <FormattedMessage id="summary-notification.dismiss" defaultMessage="Dismiss notification">
      {dismissLabel => (
        <ToastTrigger>
          <Button aria-label={dismissLabel} icon={<Dismiss20Regular />} size="small" appearance="transparent" />
        </ToastTrigger>
      )}
    </FormattedMessage>
  );
}

const defaultContextValue = {
  error: null,
  hasSummaryAgent: false,
  artifactCapabilities: null,
  canGenerateSummary: false,
  canDownloadChatHistory: false,
  canDownloadTranscriptionHistory: false,
  isChatHistoryDownloadConfigured: false,
  isTranscriptionHistoryDownloadConfigured: false,
  isConfigured: false,
  isSummaryConfigured: false,
  isGeneratingSummary: false,
  isDownloadingChatHistory: false,
  isDownloadingTranscriptionHistory: false,
  lastSummary: null,
  summaryAgentUuid: null,
  summaryDataSources: null,
  mirrorChatMessage: async () => null,
  generateSummary: async () => null,
  downloadChatHistory: async () => null,
  downloadTranscriptionHistory: async () => null
};

const RoomPersistenceContext = createContext(defaultContextValue);

export const useRoomPersistence = () => useContext(RoomPersistenceContext);

function resolveRoomId(hub, hubChannel) {
  return hub?.hub_id || hubChannel?.hubId || null;
}

function normalizeId(value) {
  return value === undefined || value === null ? null : String(value).trim() || null;
}

function resolveRoomWorkspace({ hub, store } = {}) {
  const embeddedWorkspace = hub?.workspace || hub?.workspaceInfo || hub?.workspace_data || null;
  const id =
    normalizeId(hub?.workspace_id) ||
    normalizeId(hub?.workspaceId) ||
    normalizeId(embeddedWorkspace?.id) ||
    normalizeId(embeddedWorkspace?.ulid) ||
    normalizeId(store?.state?.authUser?.last_workspace_id);

  if (!id) {
    return null;
  }

  return {
    ...(embeddedWorkspace || {}),
    id,
    user_id:
      embeddedWorkspace?.user_id || embeddedWorkspace?.userId || hub?.workspace_user_id || hub?.workspaceUserId || null
  };
}

function storageLimitScene() {
  return typeof window !== "undefined" ? window.APP?.scene : null;
}

function storageLimitErrorFromResult(result) {
  const error = new Error(result?.error || "Storage limit exceeded");
  error.status = 402;
  error.data = result;
  error.response = { status: 402, data: result };
  return error;
}

function useLatestRef(value) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

function normalizeSummaryDataSources(dataSources) {
  return Array.isArray(dataSources) && dataSources.length > 0 ? sanitizeSummaryDataSources(dataSources) : null;
}

function getNotificationsCenter() {
  return typeof window !== "undefined" ? window.notificationsCenter : null;
}

function summaryStartedToastId(summaryId) {
  return `summary-generation-${summaryId || "pending"}`;
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, Math.max(0, ms)));
}

async function waitForStartedNotification(startedNotification) {
  if (!startedNotification) {
    return;
  }

  const remainingMs = SUMMARY_STARTED_MIN_VISIBLE_MS - (Date.now() - startedNotification.startedAt);
  if (remainingMs > 0) {
    await wait(remainingMs);
  }
}

function finalSummaryToastOptions({ startedNotification, fallbackToastId }) {
  const canUpdateStartedToast = Boolean(
    startedNotification?.toastId && startedNotification.dispatched && !startedNotification.dismissed
  );
  return {
    toastId: canUpdateStartedToast ? startedNotification.toastId : fallbackToastId,
    update: canUpdateStartedToast
  };
}

function notifySummaryStarted({ summaryId } = {}) {
  const notification = {
    toastId: summaryStartedToastId(summaryId),
    startedAt: Date.now(),
    dispatched: false,
    dismissed: false
  };
  const notificationsCenter = getNotificationsCenter();
  if (!notificationsCenter?.info) {
    return notification;
  }

  notification.dispatched = true;
  notificationsCenter.info("Summary generation started", "You can keep working. We'll notify you when it's ready.", {
    timeout: 5000,
    toastId: notification.toastId,
    onStatusChange: (_event, data) => {
      if (data?.status === "dismissed" || data?.status === "unmounted") {
        notification.dismissed = true;
      }
    }
  });
  return notification;
}

function notifySummaryFailed({ toastId = "summary-generation-failed", update = false } = {}) {
  getNotificationsCenter()?.push?.(
    <Toast>
      <ToastTitle intent="error" action={renderSummaryToastDismissAction()}>
        <FormattedMessage id="summary-notification.failed.title" defaultMessage="Summary generation failed" />
      </ToastTitle>
      <ToastBody style={summaryToastBodyStyle}>
        <FormattedMessage
          id="summary-notification.failed.body"
          defaultMessage="We couldn't generate the summary. Please try again."
        />
      </ToastBody>
    </Toast>,
    { intent: "error", timeout: 7000, toastId },
    update
  );
}

function notifySummaryDownloadFailed() {
  getNotificationsCenter()?.error?.("Summary download failed", "We couldn't download the summary. Please try again.", {
    timeout: 7000
  });
}

function notifySummaryReady({
  summaryId,
  downloadFormat,
  onDownload,
  toastId = `summary-ready-${summaryId}`,
  update = false
}) {
  getNotificationsCenter()?.push?.(
    <Toast>
      <ToastTitle intent="success" action={renderSummaryToastDismissAction()}>
        <FormattedMessage id="summary-notification.ready.title" defaultMessage="Summary ready" />
      </ToastTitle>
      <ToastBody style={summaryToastBodyStyle}>
        <FormattedMessage id="summary-notification.ready.body" defaultMessage="Your summary is ready to download." />
      </ToastBody>
      <ToastFooter>
        <Link as="button" aria-label={DOWNLOAD_SUMMARY_LABEL} onClick={onDownload}>
          <FormattedMessage id="summary-notification.download" defaultMessage="Download" />
        </Link>
      </ToastFooter>
    </Toast>,
    { intent: "success", timeout: -1, toastId, downloadFormat },
    update
  );
}

function resolveSummaryAgentFromWindow() {
  if (typeof window === "undefined") {
    return null;
  }

  const agents = window.aiAgents?.getAgents?.() || [];
  return agents.find(agent => agent?.provider === OPENAI_SUMMARY_PROVIDER) || null;
}

function buildSummaryAgentFromEvent(event) {
  if (event?.detail?.provider !== OPENAI_SUMMARY_PROVIDER) {
    return null;
  }

  return {
    provider: OPENAI_SUMMARY_PROVIDER,
    objectUuid: event.detail.objectUuid || null,
    dataSources: normalizeSummaryDataSources(event.detail.dataSources)
  };
}

function resolveSummaryId(summary) {
  return summary?.id || summary?._id || summary?.data?.id || summary?.data?._id || null;
}

function resolveSummaryStatus(summary) {
  return summary?.status || summary?.data?.status || null;
}

function shouldPollSummary(summary) {
  const status = resolveSummaryStatus(summary);
  return Boolean(
    resolveSummaryId(summary) && (summary?.mode === "async" || status === "queued" || status === "processing")
  );
}

function browserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
}

function downloadDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString().slice(0, 10) : date.toISOString().slice(0, 10);
}

function downloadNamePart(value, fallback) {
  const cleaned = String(value || "")
    .split("")
    .map(char => {
      const code = char.charCodeAt(0);
      return code < 32 || '<>:"/\\|?*'.includes(char) ? " " : char;
    })
    .join("")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^[. ]+|[. ]+$/g, "")
    .trim();
  return (cleaned || fallback).slice(0, 120).trim();
}

function chatHistoryFilename(roomName) {
  const roomTitle = downloadNamePart(roomName, "Chat History");
  const title = roomTitle === "Chat History" ? roomTitle : `${roomTitle} - Chat History`;
  return `${title} - ${downloadDate()}.zip`;
}

function transcriptionHistoryFilename(roomName) {
  const roomTitle = downloadNamePart(roomName, "Transcription History");
  const title = roomTitle === "Transcription History" ? roomTitle : `${roomTitle} - Transcription History`;
  return `${title} - ${downloadDate()}.zip`;
}

function downloadBrowserFile(file, fallbackFilename = "download", { filename } = {}) {
  if (!file?.blob || typeof window === "undefined" || typeof document === "undefined" || !window.URL?.createObjectURL) {
    return;
  }

  const url = window.URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || file.filename || fallbackFilename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL?.(url);
}

async function downloadSummaryWithGrant({ roomApi, summaryId, downloadFormat, toastId }) {
  try {
    downloadBrowserFile(await roomApi.downloadSummary(summaryId, { format: downloadFormat }));
    if (toastId) {
      getNotificationsCenter()?.dismiss?.(toastId);
    }
  } catch (downloadError) {
    console.warn("[RoomPersistence] Failed to download generated summary", downloadError);
    notifySummaryDownloadFailed();
  }
}

export function RoomPersistenceProvider({
  api,
  children,
  dashboardApiUrl,
  hub,
  hubChannel,
  mySessionId,
  nestApiUrl,
  store
}) {
  const roomApi = useMemo(
    () =>
      api ||
      new RoomPersistenceApi({
        dashboardApiUrl,
        nestApiUrl
      }),
    [api, dashboardApiUrl, nestApiUrl]
  );
  const roomId = resolveRoomId(hub, hubChannel);
  const hasArtifactCapabilityIdentity = Boolean(store?.state?.credentials?.token);
  const isChatHistoryDownloadConfigured = Boolean(roomApi?.isChatHistoryDownloadConfigured?.());
  const isTranscriptionHistoryDownloadConfigured = Boolean(roomApi?.isTranscriptionHistoryDownloadConfigured?.());
  const isConfigured = Boolean(roomApi?.isConfigured?.());
  const isSummaryConfigured = Boolean(roomApi?.isSummaryConfigured?.());

  const [error, setError] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isDownloadingChatHistory, setIsDownloadingChatHistory] = useState(false);
  const [isDownloadingTranscriptionHistory, setIsDownloadingTranscriptionHistory] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);
  const [summaryAgent, setSummaryAgent] = useState(resolveSummaryAgentFromWindow);
  const [artifactCapabilities, setArtifactCapabilities] = useState(null);

  const contextRef = useLatestRef({ hub, hubChannel, mySessionId, roomId, store });
  const hasSummaryAgent = Boolean(summaryAgent);
  const summaryAgentUuid = summaryAgent?.objectUuid || summaryAgent?.id || null;
  const summaryDataSources = normalizeSummaryDataSources(summaryAgent?.dataSources);
  const summaryDataSourcesRef = useLatestRef(summaryDataSources);

  useEffect(() => {
    const handleAgentRegistered = event => {
      const summaryAgentFromWindow = resolveSummaryAgentFromWindow();
      if (summaryAgentFromWindow) {
        setSummaryAgent(summaryAgentFromWindow);
        return;
      }

      const summaryAgentFromEvent = buildSummaryAgentFromEvent(event);
      if (summaryAgentFromEvent) {
        setSummaryAgent(summaryAgentFromEvent);
      }
    };

    setSummaryAgent(resolveSummaryAgentFromWindow());
    window.addEventListener("ai-agent-registered", handleAgentRegistered);
    return () => window.removeEventListener("ai-agent-registered", handleAgentRegistered);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isConfigured || !roomId || !hasArtifactCapabilityIdentity || !roomApi.getArtifactCapabilities) {
      setArtifactCapabilities(null);
      return undefined;
    }

    roomApi
      .getArtifactCapabilities(roomId)
      .then(capabilities => {
        if (!cancelled) setArtifactCapabilities(capabilities || null);
      })
      .catch(capabilityError => {
        if (!cancelled) {
          console.warn("[RoomPersistence] Failed to load artifact capabilities", capabilityError);
          setArtifactCapabilities(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasArtifactCapabilityIdentity, isConfigured, roomApi, roomId]);

  const canGenerateSummary = artifactCapabilities?.can_generate_summary === true;
  const canDownloadChatHistory = artifactCapabilities?.can_download_chat === true;
  const canDownloadTranscriptionHistory = artifactCapabilities?.can_download_transcription === true;

  const generateSummary = useCallback(
    async (options = {}) => {
      const { downloadFormat = "pdf", ...summaryOptions } = options;
      const currentContext = contextRef.current;
      if (!isSummaryConfigured || !canGenerateSummary || !hasSummaryAgent || !currentContext.roomId) {
        return null;
      }

      const requestedByUserId = resolveRoomUserId({
        store: currentContext.store
      });
      const workspace = resolveRoomWorkspace(currentContext);
      const workspaceId = workspace?.id || null;
      const hostDisplayName = resolveRoomDisplayName({
        hubChannel: currentContext.hubChannel,
        store: currentContext.store,
        mySessionId: currentContext.mySessionId
      });

      setIsGeneratingSummary(true);
      let summaryPollingStarted = false;
      try {
        if (workspaceId && roomApi.canUploadWorkspaceStorage) {
          const canUpload = await roomApi.canUploadWorkspaceStorage(workspaceId, {
            purpose: "meeting_summary",
            format: downloadFormat
          });
          if (canUpload?.canUpload === false) {
            throw storageLimitErrorFromResult(canUpload);
          }
        }

        const summary = await roomApi.generateSummary(
          buildSummaryRequestPayload({
            roomId: currentContext.roomId,
            workspaceId,
            dataSources: summaryOptions.dataSources || summaryDataSourcesRef.current || undefined,
            summaryAgentUuid: summaryOptions.summaryAgentUuid || summaryAgentUuid,
            requestedByUserId,
            metadata: {
              hubName: currentContext.hub?.name,
              hostDisplayName,
              hostSessionId: currentContext.mySessionId
            },
            downloadFormat,
            ...summaryOptions
          })
        );
        const generatedSummaryId = resolveSummaryId(summary);
        if (generatedSummaryId && shouldPollSummary(summary) && roomApi.pollSummary) {
          summaryPollingStarted = true;
          setLastSummary(summary);
          setError(null);
          const startedNotification = notifySummaryStarted({ summaryId: generatedSummaryId });
          void (async () => {
            try {
              const completedSummary = (await roomApi.pollSummary(generatedSummaryId, { roomId: currentContext.roomId })) || summary;
              setLastSummary(completedSummary);
              setError(null);
              const completedSummaryId = resolveSummaryId(completedSummary) || generatedSummaryId;
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("summary-generated", {
                    detail: completedSummary
                  })
                );
              }
              await waitForStartedNotification(startedNotification);
              const readyToast = finalSummaryToastOptions({
                startedNotification,
                fallbackToastId: `summary-ready-${completedSummaryId}`
              });
              notifySummaryReady({
                summaryId: completedSummaryId,
                downloadFormat,
                toastId: readyToast.toastId,
                update: readyToast.update,
                onDownload: () =>
                  downloadSummaryWithGrant({
                    roomApi,
                    summaryId: completedSummaryId,
                    downloadFormat,
                    toastId: readyToast.toastId
                  })
              });
            } catch (pollError) {
              console.warn("[RoomPersistence] Failed to poll generated summary", pollError);
              setError(pollError);
              await waitForStartedNotification(startedNotification);
              const failedToast = finalSummaryToastOptions({
                startedNotification,
                fallbackToastId: `summary-failed-${generatedSummaryId}`
              });
              notifySummaryFailed(failedToast);
            } finally {
              setIsGeneratingSummary(false);
            }
          })();
          return summary;
        }
        setLastSummary(summary);
        setError(null);
        const summaryId = resolveSummaryId(summary);
        if (summaryId && roomApi.downloadSummary) {
          try {
            downloadBrowserFile(await roomApi.downloadSummary(summaryId, { format: downloadFormat }));
          } catch (downloadError) {
            console.warn("[RoomPersistence] Failed to download generated summary", downloadError);
          }
        }
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("summary-generated", {
              detail: summary
            })
          );
        }
        return summary;
      } catch (nextError) {
        if (
          handleStorageLimitError(nextError, storageLimitScene(), {
            authUser: currentContext.store?.state?.authUser,
            workspace
          })
        ) {
          setError(nextError);
          return null;
        }
        console.warn("[RoomPersistence] Failed to generate summary", nextError);
        setError(nextError);
        throw nextError;
      } finally {
        if (!summaryPollingStarted) {
          setIsGeneratingSummary(false);
        }
      }
    },
    [canGenerateSummary, contextRef, hasSummaryAgent, isSummaryConfigured, roomApi, summaryAgentUuid, summaryDataSourcesRef]
  );

  const mirrorChatMessage = useCallback(async () => null, []);

  const downloadChatHistory = useCallback(async () => {
    const currentContext = contextRef.current;
    if (!isChatHistoryDownloadConfigured || !canDownloadChatHistory || !currentContext.roomId || !roomApi.downloadChatHistory) {
      return null;
    }

    setIsDownloadingChatHistory(true);
    try {
      const file = await roomApi.downloadChatHistory({
        roomId: currentContext.roomId,
        timezone: browserTimezone()
      });
      const filename = chatHistoryFilename(currentContext.hub?.name);
      downloadBrowserFile(file, filename, { filename });
      setError(null);
      return file;
    } catch (nextError) {
      console.warn("[RoomPersistence] Failed to download chat history", nextError);
      setError(nextError);
      throw nextError;
    } finally {
      setIsDownloadingChatHistory(false);
    }
  }, [canDownloadChatHistory, contextRef, isChatHistoryDownloadConfigured, roomApi]);

  const downloadTranscriptionHistory = useCallback(async () => {
    const currentContext = contextRef.current;
    if (
      !isTranscriptionHistoryDownloadConfigured ||
      !canDownloadTranscriptionHistory ||
      !currentContext.roomId ||
      !roomApi.downloadTranscriptionHistory
    ) {
      return null;
    }

    setIsDownloadingTranscriptionHistory(true);
    try {
      const file = await roomApi.downloadTranscriptionHistory({
        roomId: currentContext.roomId,
        timezone: browserTimezone()
      });
      const filename = transcriptionHistoryFilename(currentContext.hub?.name);
      downloadBrowserFile(file, filename, { filename });
      setError(null);
      return file;
    } catch (nextError) {
      console.warn("[RoomPersistence] Failed to download transcription history", nextError);
      setError(nextError);
      throw nextError;
    } finally {
      setIsDownloadingTranscriptionHistory(false);
    }
  }, [canDownloadTranscriptionHistory, contextRef, isTranscriptionHistoryDownloadConfigured, roomApi]);

  const value = useMemo(
    () => ({
      error,
      hasSummaryAgent,
      artifactCapabilities,
      canGenerateSummary,
      canDownloadChatHistory,
      canDownloadTranscriptionHistory,
      isChatHistoryDownloadConfigured,
      isTranscriptionHistoryDownloadConfigured,
      isConfigured,
      isSummaryConfigured,
      isGeneratingSummary,
      isDownloadingChatHistory,
      isDownloadingTranscriptionHistory,
      lastSummary,
      summaryAgentUuid,
      summaryDataSources,
      mirrorChatMessage,
      generateSummary,
      downloadChatHistory,
      downloadTranscriptionHistory
    }),
    [
      error,
      hasSummaryAgent,
      artifactCapabilities,
      canGenerateSummary,
      canDownloadChatHistory,
      canDownloadTranscriptionHistory,
      downloadChatHistory,
      downloadTranscriptionHistory,
      isChatHistoryDownloadConfigured,
      isTranscriptionHistoryDownloadConfigured,
      isDownloadingChatHistory,
      isDownloadingTranscriptionHistory,
      isConfigured,
      isGeneratingSummary,
      isSummaryConfigured,
      lastSummary,
      summaryAgentUuid,
      summaryDataSources,
      mirrorChatMessage,
      generateSummary
    ]
  );

  return <RoomPersistenceContext.Provider value={value}>{children}</RoomPersistenceContext.Provider>;
}

RoomPersistenceProvider.propTypes = {
  api: PropTypes.object,
  children: PropTypes.node.isRequired,
  dashboardApiUrl: PropTypes.string,
  hub: PropTypes.object,
  hubChannel: PropTypes.object,
  mySessionId: PropTypes.string,
  nestApiUrl: PropTypes.string,
  store: PropTypes.object
};
